import { Badge, Link, Menu, MenuButton, IconButton, HStack, Icon, Tooltip, Box } from '@chakra-ui/react'
import { ICourse } from '@/shared/ICourse'
import { useNavigate } from 'react-router'
import { Link as RouterLink } from 'react-router-dom'
import { FiMoreHorizontal, FiAlertTriangle } from 'react-icons/fi'
import { formatDate, formatDateFromNow, highlightText } from '@/shared/parse'
import { WorkflowService } from '../../shared/WorkflowService'

import { CellContext, createColumnHelper, VisibilityState, ColumnOrderState } from "@tanstack/react-table";
import { DataTable } from "./DataTable";
import { CommentSetBadge } from './CommentSetBadge'
import { AuthService } from '../services/AuthService'
import { LabelAwarded } from '../course/LabelAwarded'
import { WorkflowTextService } from '@/shared/WorkflowTextService'
import { CourseMenuList } from '../course/CourseMenuList'
import { EmailLink } from '../components/EmailLink'
import { HighlightedText } from '../components/HighlightedText'
import { ArchivedBadge } from '../components/ArchivedBadge'
import { NoticeBadge } from '../components/NoticeBadge'

// Helper functions for user sorting and rendering
const sortUsersByName = (users: any[]) => {
  return [...users].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
};

const renderSortedUserList = (users: any[] | undefined, filter: string[]) => {
  const sortedUsers = sortUsersByName(users || []);
  return sortedUsers.map(u => <EmailLink key={u.id} name={highlightText(u.name, filter)} email={u.email} />);
};

// Helper function to create a sort key from user array
const createUserSortKey = (users: any[] | undefined): string => {
  if (!users || users.length === 0) return '';
  if (users.length === 1) return users[0].name || '';
  return users
    .map(u => u.name || '')
    .sort()
    .join(', ');
};

const renderDate = (info: CellContext<ICourse, Date | undefined>) => {
  const value = info.getValue();
  return value ? formatDate(value) : ''
}

export const CourseTable = (props: {
  filter: string[];
  courses: ICourse[];
  onDeleteCourse: (course: ICourse) => void;
  onUpdateCourse: (course: ICourse) => void;
  onInviteExpert: (course: ICourse) => void;
  onNewRepeatedCourse: (course: ICourse) => void;
  onRemindProvider: (course: ICourse) => void;
  onCourseDeadlines: (course: ICourse) => void;
  onArchiveCourse: (course: ICourse) => void;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (updater: VisibilityState | ((old: VisibilityState) => VisibilityState)) => void;
  columnOrder?: ColumnOrderState;
  onColumnOrderChange?: (updater: ColumnOrderState | ((old: ColumnOrderState) => ColumnOrderState)) => void;
}) => {

  const navigate = useNavigate();

  const openCourse = (course: ICourse) => {
    navigate(`/course/${course.id}`);
  }

  const columnHelper = createColumnHelper<ICourse>(); const columns = [
    columnHelper.accessor("id", {
      header: "ID",
      cell: (info) => {
        const course = info.row.original;
        return AuthService.isStaff &&
          <Menu isLazy>
            <MenuButton as={IconButton} variant='ghost' icon={<FiMoreHorizontal />} cursor='pointer' boxSize="10" />
            <CourseMenuList course={course}
              onUpdateCourse={props.onUpdateCourse}
              onDeleteCourse={props.onDeleteCourse}
              onInviteExpert={props.onInviteExpert}
              onNewRepeatedCourse={props.onNewRepeatedCourse}
              onRemindProvider={props.onRemindProvider}
              onCourseDeadlines={props.onCourseDeadlines}
              onArchiveCourse={props.onArchiveCourse}
            />
          </Menu>
      },
      enableSorting: false,
      enableHiding: false,
      size: 10
    }),
    columnHelper.accessor("folderName", {
      cell: (info) => {
        const course = info.row.original;
        return (
          <Link as={RouterLink} to={`/course/${course.id}`} fontWeight='600'>
            <HighlightedText text={course.folderName} filter={props.filter} />
          </Link>
        )
      },
      header: "Identifier",
    }),
    columnHelper.accessor("CourseStep", {
      cell: (info) => {
        const course = info.row.original;
        return course.CourseStep &&
          <Badge size="sm" colorScheme={WorkflowTextService.getCourseBadgeColor(course.CourseStep)}>
            <HighlightedText text={WorkflowService.getCourseBadgeLabel(course)} filter={props.filter} />
          </Badge>
      },
      header: "Step",
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.CourseStep || '';
        const b = rowB.original.CourseStep || '';
        return WorkflowTextService.compareCourseSteps(a, b);
      }
    }),
    columnHelper.accessor("provider", {
      cell: (info) => {
        const course = info.row.original;
        return course.provider?.ProviderIdentifier;
      },
      header: "Provider"
    }),
    columnHelper.accessor("CourseTitle", {
      cell: (info) => {
        const course = info.row.original;
        return (
          <HStack>
            <Link as={RouterLink} to={`/course/${course.id}`} fontWeight='600'>
              <HighlightedText text={course.CourseTitle} filter={props.filter} />
            </Link>
            <LabelAwarded award={course?.LabelAwarded} />
            <ArchivedBadge archived={course?.archived} />
            <NoticeBadge
              notice={course.CourseNotice}
              onClick={() => props.onUpdateCourse(course)}
            />
          </HStack>
        );
      },
      header: "Title",
      minSize: 64
    }),
    columnHelper.accessor((course) => createUserSortKey(course.CourseProviderUsers), {
      id: "CourseProviderUsers",
      cell: (info) => {
        const course = info.row.original;
        return renderSortedUserList(course.CourseProviderUsers, props.filter);
      },
      header: "Provider Users"
    }),
    columnHelper.accessor((course) => createUserSortKey(course.CourseStaffUsers), {
      id: "CourseStaffUsers",
      cell: (info) => {
        const course = info.row.original;
        return renderSortedUserList(course.CourseStaffUsers, props.filter);
      },
      header: "Staff Users"
    }),
    columnHelper.accessor((course) => course.expert?.name || '', {
      id: "expert",
      cell: (info) => {
        const course = info.row.original;
        const roles = AuthService.getCourseRoles(course);
        if (course.expert && WorkflowTextService.isExpertVisible(roles)) {
          return <EmailLink name={highlightText(course.expert.name, props.filter)} email={course.expert.email} />
        }
      },
      header: "External Reviewer"
    }),
    columnHelper.accessor("commentSet", {
      cell: (info) => {
        const course = info.row.original;
        return <CommentSetBadge course={course} />
      },
      header: "Comments"
    }),
    columnHelper.accessor("modified", {
      cell: (info) => {
        const course = info.row.original;
        return formatDateFromNow(course.modified)
      },
      header: "Modified"
    }),
    columnHelper.accessor((course) => {
      // Use hasBeenReviewed field from course (set when step is submitted/reviewed)
      const hasBeenReviewed = course.hasBeenReviewed ?? false;
      return WorkflowService.getCurrentDeadline(course, hasBeenReviewed);
    }, {
      id: "earliestDeadlineDate",
      cell: (info) => {
        const deadline = info.getValue() as Date | null | undefined;
        const deadlineText = formatDateFromNow(deadline);
        const isOverdue = deadline && new Date(deadline) < new Date();

        return isOverdue ? (
          <HStack spacing={1}>
            <Tooltip label="Deadline has passed" aria-label="Deadline passed" openDelay={500}>
              <Box display="flex" alignItems="center">
                <Icon as={FiAlertTriangle} color="red.500" boxSize={4} />
              </Box>
            </Tooltip>
            <span>{deadlineText}</span>
          </HStack>
        ) : deadlineText;
      },
      header: "Deadline"
    }),
    // Individual deadline columns (hidden by default)
    columnHelper.accessor((course) => course.deadlines?.step1DraftSubmissionDate, {
      id: "step1DraftSubmissionDate",
      cell: renderDate,
      header: "Step 1 Draft Submission"
    }),
    columnHelper.accessor((course) => course.deadlines?.step1InternalReviewDate, {
      id: "step1InternalReviewDate",
      cell: renderDate,
      header: "Step 1 Internal Review"
    }),
    columnHelper.accessor((course) => course.deadlines?.step1FinalSubmissionDate, {
      id: "step1FinalSubmissionDate",
      cell: renderDate,
      header: "Step 1 Final Submission"
    }),
    columnHelper.accessor((course) => course.deadlines?.step2DraftSubmissionDate, {
      id: "step2DraftSubmissionDate",
      cell: renderDate,
      header: "Step 2 Draft Submission"
    }),
    columnHelper.accessor((course) => course.deadlines?.step2InternalReviewDate, {
      id: "step2InternalReviewDate",
      cell: renderDate,
      header: "Step 2 Internal Review"
    }),
    columnHelper.accessor((course) => course.deadlines?.step2FinalSubmissionDate, {
      id: "step2FinalSubmissionDate",
      cell: renderDate,
      header: "Step 2 Final Submission"
    }),
    columnHelper.accessor((course) => course.modifiedBy?.name, {
      cell: (info) => {
        const course = info.row.original;
        return renderSortedUserList([course.modifiedBy], props.filter);
      },
      header: "Modified By"
    }),
    columnHelper.accessor("CourseSegment", {
      header: "Segment"
    }),
    columnHelper.accessor("coursePlatformVersion", {
      cell: (info) => {
        const course = info.row.original;
        // V1 reports may have empty version data, so show V1 for empty/null values
        return course.coursePlatformVersion || "V1";
      },
      header: "Platform Version"
    }),
    columnHelper.accessor("CourseNotice", {
      cell: (info) => {
        const course = info.row.original;
        if (!AuthService.isStaff) return null;

        const notice = course.CourseNotice;
        if (!notice) return '';

        // Show truncated notice with tooltip for longer text
        const truncated = notice.length > 50 ? notice.substring(0, 50) + '...' : notice;
        return (
          <Tooltip label={notice} aria-label="Full notice">
            <Box><HighlightedText text={truncated} filter={props.filter} /></Box>
          </Tooltip>
        );
      },
      header: "Notice"
    }),
  ];

  return (
    <DataTable
      onRowClick={openCourse}
      columns={columns}
      emptyLabel='No courses to show here yet'
      data={props.courses}
      columnVisibility={props.columnVisibility}
      onColumnVisibilityChange={props.onColumnVisibilityChange}
      columnOrder={props.columnOrder}
      onColumnOrderChange={props.onColumnOrderChange}
    />
  )
}